/**
 * "ADD A VIDEO" (0144): a file or a YouTube link, by the video editor's own panes, under a required
 * tick of the video guidelines, recorded with the video. Nothing can be chosen before the tick. The
 * block id is minted once for the dialog, so a retried attach names the same block; the block goes
 * into the document only once the platform has set its video (./BlockList.tsx).
 */
import { Anchor, Checkbox, Modal, SegmentedControl, Stack, Text } from '@mantine/core';
import { useCallback, useState } from 'react';
import { MAX_VIDEO_BLOCKS } from '../../../../../../shared/content-model/blocks';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import type { ArticleVideo } from '../../../features/cabinet/api/editor-api';
import { hasErrorCode, isApiProblem } from '../../../features/cabinet/api/problem';
import { useAction } from '../../../features/cabinet/state';
import { useTranslation } from '../../../i18n';
import { UploadPane, YoutubePane } from '../../videos/VideoBlock';
import { VIDEO_GUIDELINES_HREF } from '../../videos/VideoRights';
import { useVideoUpload } from '../../videos/use-video-upload';
import type { UploadedVideoFile } from '../../videos/use-video-upload';
import { describeVideoUploadFailure } from '../../videos/video-words';
import { newBlockId } from './document';

export interface VideoBlockDialogProps {
  readonly articleId: string;
  /** The block whose video is replaced; absent for a new block. */
  readonly blockId?: string;
  /** The server's own instant a new block's id is minted from (./document.ts). */
  readonly serverInstant: string;
  readonly onSet: (blockId: string, video: ArticleVideo) => void;
  readonly onClose: () => void;
}

export function VideoBlockDialog({
  articleId,
  blockId,
  serverInstant,
  onSet,
  onClose,
}: VideoBlockDialogProps) {
  const { t } = useTranslation('video');
  const { t: ta } = useTranslation('articles');
  const api = useEditorApi();
  const [id] = useState(() => blockId ?? newBlockId(serverInstant));
  const [source, setSource] = useState<'upload' | 'youtube'>('upload');
  const [ticked, setTicked] = useState(false);

  const attachFile = useCallback(
    (file: UploadedVideoFile) =>
      api.setArticleVideoBlock(articleId, id, {
        source: 'upload',
        intakeId: file.intakeId,
        originalName: file.fileName,
        byteSize: file.byteSize,
        rulesConfirmed: true,
      }),
    [api, articleId, id],
  );
  const upload = useVideoUpload(articleId, attachFile);
  const attachLink = useAction((youtubeUrl: string) =>
    api.setArticleVideoBlock(articleId, id, {
      source: 'youtube',
      youtubeUrl,
      rulesConfirmed: true,
    }),
  );
  const goingIn = upload.state.phase === 'uploading' || upload.state.phase === 'attaching';

  const onFile = (file: File) => {
    void (async () => {
      const video = await upload.upload(file);
      if (video !== undefined) {
        onSet(id, video);
      }
    })();
  };
  const onRetryAttach = () => {
    void (async () => {
      const video = await upload.retryAttach();
      if (video !== undefined) {
        onSet(id, video);
      }
    })();
  };
  const onLink = (url: string) => {
    void (async () => {
      const video = await attachLink.run(url);
      if (video !== undefined) {
        onSet(id, video);
      }
    })();
  };

  // A file's 422 here is the article's limit: `describeVideoUploadFailure` would say it is no video.
  const describeFailure = (error: unknown) =>
    isApiProblem(error) &&
    error.status === 422 &&
    !hasErrorCode(error, 'UPLOAD_TOO_LARGE') &&
    !hasErrorCode(error, 'VIDEO_UPLOADS_BUSY')
      ? ta('editor.blocks.video.limit', { max: String(MAX_VIDEO_BLOCKS) })
      : describeVideoUploadFailure(error, t);

  return (
    <Modal
      opened
      centered
      size="lg"
      title={
        blockId === undefined
          ? ta('editor.blocks.video.title')
          : ta('editor.blocks.video.titleAnother')
      }
      closeButtonProps={{ 'aria-label': ta('common.dismiss') }}
      onClose={() => {
        upload.cancel();
        onClose();
      }}
    >
      <Stack gap="sm">
        <Checkbox
          required
          checked={ticked}
          disabled={goingIn}
          label={ta('editor.blocks.video.rulesTick')}
          description={
            <Anchor href={VIDEO_GUIDELINES_HREF} target="_blank" rel="noreferrer" size="xs">
              {t('rights.guidelines')}
            </Anchor>
          }
          onChange={(event) => {
            setTicked(event.currentTarget.checked);
          }}
        />
        <SegmentedControl
          aria-label={t('block.sourceLabel')}
          value={source}
          disabled={goingIn}
          onChange={(value) => {
            setSource(value === 'youtube' ? 'youtube' : 'upload');
          }}
          data={[
            { value: 'upload', label: t('block.sourceUpload') },
            { value: 'youtube', label: t('block.sourceYoutube') },
          ]}
        />
        {source === 'upload' ? (
          <UploadPane
            upload={upload}
            disabled={!ticked}
            onFile={onFile}
            onCancel={upload.cancel}
            onRetryAttach={onRetryAttach}
            describeFailure={describeFailure}
            checkedNote={ta('editor.blocks.video.watchedAfter')}
            t={t}
          />
        ) : (
          <YoutubePane
            disabled={!ticked}
            busy={attachLink.isRunning}
            error={attachLink.error}
            onAdd={onLink}
            kinds={t('youtube.kinds')}
            checkedNote={ta('editor.blocks.video.channelAfter')}
            t={t}
          />
        )}
        <Text size="xs" c="dimmed">
          {ta('editor.blocks.video.appNote')}
        </Text>
      </Stack>
    </Modal>
  );
}
